import { db, type MediaRow } from '../db';
import { supabase } from '../supabase/browserClient';
import { isSupabaseSyncEnabled } from '../supabase/config';
import { queueForSync, removeOutboxEntryByRecord } from './syncOutbox';
import { REPORT_ATTACHMENT_OUTBOX_TABLE } from './syncConstants';
import { newId } from '@/utils/ids';

export const REPORT_ATTACHMENT_BUCKET = 'report-attachments';

export type ReportAttachmentOutboxPayload = {
  reportId: string;
  mediaId: string;
  storagePath: string;
  mimeType?: string;
  byteSize?: number;
};

export function buildReportAttachmentPath(
  reportId: string,
  mediaId: string,
  extension = 'jpg'
): string {
  return `${reportId}/${mediaId}.${extension}`;
}

export function normalizeStoragePath(storagePath: string): string {
  let trimmed = storagePath.trim().replace(/^\/+/, '');
  if (trimmed.startsWith(`${REPORT_ATTACHMENT_BUCKET}/`)) {
    trimmed = trimmed.slice(REPORT_ATTACHMENT_BUCKET.length + 1);
  }
  return trimmed;
}

function parseStoragePath(storagePath: string): {
  reportId?: string;
  mediaId?: string;
} {
  const normalized = normalizeStoragePath(storagePath);
  const [reportId, fileName] = normalized.split('/', 2);
  if (!reportId || !fileName) {
    return {};
  }
  const mediaId = fileName.split('.', 1)[0];
  return {
    reportId,
    mediaId: mediaId || undefined,
  };
}

async function upsertMediaRow(
  row: Omit<MediaRow, 'createdAt' | 'updatedAt'> & {
    createdAt?: string;
    updatedAt?: string;
  }
): Promise<void> {
  const now = new Date().toISOString();
  await db.media.put({
    ...row,
    createdAt: row.createdAt ?? now,
    updatedAt: row.updatedAt ?? now,
  });
}

export async function queueReportAttachmentUpload(
  payload: ReportAttachmentOutboxPayload & { dataUrl: string }
): Promise<void> {
  const normalizedPath = normalizeStoragePath(payload.storagePath);
  const { blob, mimeType } = dataUrlToBlob(payload.dataUrl);
  const resolvedMimeType = payload.mimeType ?? mimeType ?? 'image/jpeg';
  const resolvedByteSize = payload.byteSize ?? blob.size;
  await upsertMediaRow({
    id: payload.mediaId,
    reportId: payload.reportId,
    storagePath: normalizedPath,
    blob,
    mimeType: resolvedMimeType,
    byteSize: resolvedByteSize,
  });
  const outboxPayload: ReportAttachmentOutboxPayload = {
    reportId: payload.reportId,
    mediaId: payload.mediaId,
    storagePath: normalizedPath,
    mimeType: resolvedMimeType,
    byteSize: resolvedByteSize,
  };
  await queueForSync(REPORT_ATTACHMENT_OUTBOX_TABLE, normalizedPath, 'upsert', outboxPayload, {
    coalesce: false,
    idempotencyKey: payload.mediaId,
  });
}

export async function removeQueuedReportAttachment(storagePath: string): Promise<void> {
  await removeOutboxEntryByRecord(
    REPORT_ATTACHMENT_OUTBOX_TABLE,
    normalizeStoragePath(storagePath)
  );
}

export async function getPendingReportAttachmentPaths(reportId: string): Promise<Set<string>> {
  const rows = await db.syncOutbox
    .filter(
      (row) =>
        row.table === REPORT_ATTACHMENT_OUTBOX_TABLE && row.recordId.startsWith(`${reportId}/`)
    )
    .toArray();
  return new Set(rows.map((row) => row.recordId));
}

export async function uploadReportAttachment(
  payload: ReportAttachmentOutboxPayload
): Promise<void> {
  if (!isSupabaseSyncEnabled()) {
    throw new Error('Supabase sync disabled for attachment upload.');
  }
  if (!supabase) {
    throw new Error('Supabase client not configured for attachment upload.');
  }

  const normalizedPath = normalizeStoragePath(payload.storagePath);
  const media = await db.media.get(payload.mediaId);
  if (!media?.blob) {
    throw new Error('Media blob not available for upload.');
  }
  const { error } = await supabase.storage
    .from(REPORT_ATTACHMENT_BUCKET)
    .upload(normalizedPath, media.blob, {
      contentType: payload.mimeType ?? media.mimeType ?? 'image/jpeg',
      upsert: true,
    });

  if (error) {
    throw new Error(error.message);
  }

  await db.media.update(payload.mediaId, {
    blob: null,
    updatedAt: new Date().toISOString(),
  });
}

export async function getCachedReportAttachment(storagePath: string): Promise<string | null> {
  try {
    const normalizedPath = normalizeStoragePath(storagePath);
    const entry = await db.media.where('storagePath').equals(normalizedPath).first();
    if (!entry?.blob) return null;
    return await blobToDataUrl(entry.blob);
  } catch {
    return null;
  }
}

export async function cacheReportAttachment(storagePath: string, dataUrl: string): Promise<void> {
  try {
    const normalizedPath = normalizeStoragePath(storagePath);
    const { blob, mimeType } = dataUrlToBlob(dataUrl);
    const { reportId, mediaId } = parseStoragePath(normalizedPath);
    await upsertMediaRow({
      id: mediaId ?? newId(),
      reportId: reportId ?? 'unknown',
      storagePath: normalizedPath,
      blob,
      mimeType: mimeType ?? 'image/jpeg',
      byteSize: blob.size,
    });
  } catch {
    // Ignore cache write errors (e.g., missing IndexedDB in tests).
  }
}

export async function fetchReportAttachment(storagePath: string): Promise<string | null> {
  if (!storagePath) return null;
  const cached = await getCachedReportAttachment(storagePath);
  if (cached) return cached;

  if (!isSupabaseSyncEnabled()) return null;
  if (!supabase) return null;

  const normalizedPath = normalizeStoragePath(storagePath);
  const { data, error } = await supabase.storage
    .from(REPORT_ATTACHMENT_BUCKET)
    .download(normalizedPath);

  if (error || !data) {
    return null;
  }

  const dataUrl = await blobToDataUrl(data);
  await cacheReportAttachment(normalizedPath, dataUrl);
  return dataUrl;
}

export function dataUrlToBlob(dataUrl: string): {
  blob: Blob;
  mimeType?: string;
} {
  const [header, base64] = dataUrl.split(',', 2);
  if (!header || !base64) {
    throw new Error('Invalid data URL');
  }
  const match = header.match(/data:(.*?);base64/);
  const mimeType = match?.[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return {
    blob: new Blob([bytes], { type: mimeType ?? 'application/octet-stream' }),
    mimeType,
  };
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
